(ns practitest.frontend.components.view-editor.basic-builder
  "Basic-mode UI for the view editor. Walks AST, emits PTQL."
  (:require
    [clojure.string :as str]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.selector2 :as selector2]
    [practitest.frontend.components.view-editor.events :as-alias events]
    [practitest.frontend.components.view-editor.subs :as-alias view-editor.subs]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

(def ^:private MAX-DEPTH 3)

(def ^:private comparison-ops
  ;; AST ops that map cleanly to a single basic-mode comparison row
  ;; NOTE: there is no "is-not" anymore, inequality is ["not", ["is", ...]]
  #{"is" "contains" "starts-with"
    "gt" "gte" "lt" "lte"
    "in-last" "empty" "not-empty"})

(def ^:private negatable-ops
  ;; Comparisons that have a dedicated NOT-form in PTQL
  ;; Surfaced in basic UI as "is not", "does not contain", "does not start with"
  #{"is" "contains" "starts-with" "in-last"})

(def ^:private keywords
  #{"AND" "OR" "NOT" "IS" "EMPTY" "IN" "LAST" "LINKED" "TO" "WHERE"
    "STARTS" "WITH" "CONTAINS" "CURRENT_USER" "GROUP" "VIEW" "COUNT"})

;; ---- shape predicates ----

(defn group? [ast]
  (and (vector? ast)
       (#{"and" "or"} (first ast))))

(defn comparison? [ast]
  (and (vector? ast)
       (comparison-ops (first ast))))

(defn- linked-to? [ast]
  (and (vector? ast)
       (= "exists-related" (first ast))))

(defn- count-node? [ast]
  (and (vector? ast)
       (= "count" (first ast))))

(defn not-node? [ast]
  (and (vector? ast)
       (= "not" (first ast))))

(defn negated-comparison? [ast]
  (and (not-node? ast)
       (vector? (second ast))
       (negatable-ops (first (second ast)))))

(defn- negated-linked-to? [ast]
  (and (not-node? ast)
       (linked-to? (second ast))))

;; ---- view-fits-basic? ----

(declare fits?)

(defn- comparison-fits? [[_op field _value]]
  ;; LHS must be a plain field name (string). Excludes count(...) LHS.
  (string? field))

(defn fits?
  "True iff the basic-mode editor can handle this AST.
  Currently: nil (empty), single (possibly negated) comparison, AND/OR groups
  of comparisons, nested up to MAX-DEPTH. LINKED TO and COUNT not yet."
  ([ast] (fits? ast 1))
  ([ast depth]
   (cond
     (nil? ast) true
     (count-node? ast) false
     (negated-comparison? ast) (comparison-fits? (second ast))
     (not-node? ast) false
     (comparison? ast) (comparison-fits? ast)
     (group? ast) (and (<= depth MAX-DEPTH)
                       (seq (rest ast))
                       (every? #(fits? % (inc depth)) (rest ast)))
     :else false)))

;; ---- operators for UI ----

(defn- normalize-field-type [t]
  (case t
    "memo" "text"
    ("linkedlist" "multilist") "list"
    "date-time" "datetime"
    t))

(def ^:private all-types
  #{"text" "number" "list" "user" "date" "datetime"})

(def op-codes
  ;; Order is dropdown order
  [{:code "="
    :op "is"
    :negated? false
    :arity :binary
    :types all-types}
   {:code "≠"
    :op "is"
    :negated? true
    :arity :binary
    :types all-types}
   {:code "contains"
    :op "contains"
    :negated? false
    :arity :binary
    :types #{"text"}}
   {:code "does not contain"
    :op "contains"
    :negated? true
    :arity :binary
    :types #{"text"}}
   {:code "starts with"
    :op "starts-with"
    :negated? false
    :arity :binary
    :types #{"text"}}
   {:code "does not start with"
    :op "starts-with"
    :negated? true
    :arity :binary
    :types #{"text"}}
   {:code ">"
    :op "gt"
    :negated? false
    :arity :binary
    :types #{"number" "date" "datetime"}}
   {:code "≥"
    :op "gte"
    :negated? false
    :arity :binary
    :types #{"number" "date" "datetime"}}
   {:code "<"
    :op "lt"
    :negated? false
    :arity :binary
    :types #{"number" "date" "datetime"}}
   {:code "≤"
    :op "lte"
    :negated? false
    :arity :binary
    :types #{"number" "date" "datetime"}}
   {:code "in last"
    :op "in-last"
    :negated? false
    :arity :in-last
    :types #{"date" "datetime"}}
   {:code "not in last"
    :op "in-last"
    :negated? true
    :arity :in-last
    :types #{"date" "datetime"}}
   {:code "is empty"
    :op "empty"
    :negated? false
    :arity :unary
    :types all-types}
   ;; NOTE: not-empty is still a separate operator in AST
   {:code "is not empty"
    :op "not-empty"
    :negated? false
    :arity :unary
    :types all-types}])

(def ^:private op-by-code
  (into {} (map (juxt :code identity)) op-codes))

(def ^:private op-code-by-ast
  (into {}
        (map (fn [{:keys [code op negated?]}] [[op negated?] code]))
        op-codes))

(def ^:private all-op-codes (set (map :code op-codes)))

(defn op-arity [op-code]
  (:arity (op-by-code op-code)))

(defn valid-ops-for-type [field-type]
  (let [t (normalize-field-type field-type)]
    (if (contains? all-types t)
      (->> op-codes (filter #(contains? (:types %) t)) (map :code) set)
      all-op-codes)))

(defn clause-field-id [clause]
  (if (not-node? clause)
    (clause-field-id (second clause))
    (second clause)))

(defn clause-value [clause]
  (if (not-node? clause)
    (clause-value (second clause))
    (nth clause 2 nil)))

(defn clause-op-code [clause]
  (when (or (comparison? clause) (negated-comparison? clause))
    (let [negated? (not-node? clause)
          inner (if negated? (second clause) clause)]
      (op-code-by-ast [(first inner) negated?]))))

(defn clause? [node]
  (or (comparison? node) (negated-comparison? node)))

(defn assoc-clause-field-id [clause field-id]
  (if (not-node? clause)
    (update clause 1 assoc-clause-field-id field-id)
    (assoc clause 1 field-id)))

(defn assoc-clause-value [clause value]
  (if (not-node? clause)
    (update clause 1 assoc-clause-value value)
    (assoc clause 2 value)))

(defn wrap-as-group [ast]
  (cond
    (nil? ast) ["and"]
    (group? ast) ast
    :else ["and" ast]))

(defn display-name-fn-for
  "Build `(fn [field-id] -> display-name)` for an entity 
  from `fields-by-ptql-entity` sub data."
  [fields-by-entity entity]
  (let [by-id (into {}
                    (map (juxt :id :name))
                    (get fields-by-entity entity []))]
    (fn [field-id]
      (get by-id field-id field-id))))

(defn empty-clause []
  ["is" nil nil])

(defn empty-group
  ([] (empty-group :and))
  ([combinator] [(name combinator)]))

(defn build-clause [op-code field-id value]
  (let [{:keys [op negated? arity]} (op-by-code op-code)
        clause (if (= :unary arity)
                 [op field-id]
                 [op field-id value])]
    (if negated?
      ["not" clause]
      clause)))

(defn change-op [clause new-op-code]
  (let [old-arity (op-arity (clause-op-code clause))
        new-arity (op-arity new-op-code)]
    (build-clause new-op-code
                  (clause-field-id clause)
                  (cond
                    (= :unary new-arity) nil
                    (= old-arity new-arity) (clause-value clause)
                    (= :in-last new-arity) {"unit" "days"}
                    :else nil))))

(defn clause-complete? [clause]
  (let [op-code (clause-op-code clause)
        arity (op-arity op-code)]
    (and (some? (clause-field-id clause))
         (case arity
           :unary true
           :binary (some? (clause-value clause))
           :in-last (let [v (clause-value clause)]
                      (and (map? v) (some? (get v "n")) (not (str/blank? (get v "unit")))))))))

(defn has-incomplete-clauses? [ast]
  (cond
    (nil? ast) false
    (group? ast) (boolean (some has-incomplete-clauses? (rest ast)))
    (or (comparison? ast) (negated-comparison? ast)) (not (clause-complete? ast))
    :else false))

(defn valid-ast
  "Walk the working AST and produce the canonical AST for save/PTQL.
  Drops incomplete clauses, drops empty groups, collapses single-child groups.
  Returns nil if nothing valid is left."
  [ast]
  (cond
    (nil? ast)
    nil

    (group? ast)
    (let [kept (keep valid-ast (rest ast))]
      (case (count kept)
        0 nil
        1 (first kept)
        (vec (cons (first ast) kept))))

    (or (comparison? ast) (negated-comparison? ast))
    (when (clause-complete? ast) ast)

    :else nil))

(defn- path->indices [path]
  (mapv inc path))

(defn get-at [ast path]
  (get-in ast (path->indices path)))

(defn update-at [ast path f & args]
  (if (empty? path)
    (apply f ast args)
    (apply update-in ast (path->indices path) f args)))

(defn- vec-without [v i]
  (into (subvec v 0 i) (subvec v (inc i))))

(defn remove-at [ast path]
  (let [parent (vec (butlast path))
        i (last path)]
    (update-at ast parent
               (fn [[op & children]]
                 (into [op] (vec-without (vec children) i))))))

(defn append-child [ast path child]
  (update-at ast path #(conj (vec %) child)))

(defn set-clause-field [ast path field-id field-type]
  (let [with-field (update-at ast path assoc-clause-field-id field-id)
        clause (get-at with-field path)
        op (clause-op-code clause)
        valid (valid-ops-for-type field-type)]
    (if (and op (not (valid op)))
      (update-at with-field path change-op "=")
      with-field)))

;; ---- ast -> PTQL string ----

(defn- field-token [display-name]
  (if (and (re-matches #"[a-zA-Z_]\w*" display-name)
           (not (keywords (str/upper-case display-name))))
    display-name
    (str "'" display-name "'")))

(defn- emit-field [display-name-fn field-id]
  (field-token (display-name-fn field-id)))

(defn- emit-value [v]
  (cond
    (nil? v) ""
    (number? v) (str v)
    (map? v) (cond
               (= "current-user" (get v "fn")) "CURRENT_USER"
               (= "group" (get v "fn")) (str "GROUP \"" (get v "name") "\"")
               :else (str v))
    :else (str "\"" v "\"")))

(declare emit-with-parent)

(defn- emit-comparison [display-name-fn [op field value]]
  (let [f (emit-field display-name-fn field)]
    (case op
      "is" (str f " = " (emit-value value))
      "contains" (str f " CONTAINS " (emit-value value))
      "starts-with" (str f " STARTS WITH " (emit-value value))
      "gt" (str f " > " (emit-value value))
      "gte" (str f " >= " (emit-value value))
      "lt" (str f " < " (emit-value value))
      "lte" (str f " <= " (emit-value value))
      "empty" (str f " IS EMPTY")
      "not-empty" (str f " IS NOT EMPTY")
      "in-last" (str f " IN LAST " (get value "n") " " (get value "unit")))))

(defn- emit-negated-comparison [display-name-fn [_ [op field value]]]
  (let [f (emit-field display-name-fn field)]
    (case op
      "is" (str f " != " (emit-value value))
      "contains" (str f " NOT CONTAINS " (emit-value value))
      "starts-with" (str f " NOT STARTS WITH " (emit-value value))
      "in-last" (str f " NOT IN LAST " (get value "n") " " (get value "unit")))))

(defn- emit-linked-to [display-name-fn linked-display-name-fn-for [_ entity inner-ast] negated?]
  (let [prefix (if negated? "NOT LINKED TO " "LINKED TO ")
        inner-fn (linked-display-name-fn-for entity)]
    (cond-> (str prefix entity)
      inner-ast (str " WHERE (" (emit-with-parent inner-fn linked-display-name-fn-for inner-ast nil) ")"))))

(defn- emit-group [display-name-fn linked-display-name-fn-for [op & children] parent-op]
  (let [sep (case op "and" " AND " "or" " OR ")
        body (->> children
                  (map #(emit-with-parent display-name-fn linked-display-name-fn-for % op))
                  (str/join sep))
        needs-parens? (or (and (= op "and") (= parent-op "or"))
                          (and (= op "or") (some? parent-op)))]
    (if needs-parens? (str "(" body ")") body)))

(defn- emit-with-parent [display-name-fn linked-display-name-fn-for ast parent-op]
  (cond
    (nil? ast) ""
    (negated-comparison? ast) (emit-negated-comparison display-name-fn ast)
    (negated-linked-to? ast) (emit-linked-to display-name-fn linked-display-name-fn-for (second ast) true)
    (linked-to? ast) (emit-linked-to display-name-fn linked-display-name-fn-for ast false)
    (comparison? ast) (emit-comparison display-name-fn ast)
    (group? ast) (emit-group display-name-fn linked-display-name-fn-for ast parent-op)))

(defn ast->ptql
  "Emit PTQL from an AST that fits basic editor.
  
  Parameters:
  - `display-name-fn`: `(fn [field-id] -> display-name)` for source entity
  - `linked-display-name-fn`: `(fn [entity-name] -> display-name-fn)` for LINKED TO inner clauses"
  [display-name-fn linked-display-name-fn-for ast]
  (emit-with-parent display-name-fn linked-display-name-fn-for ast nil))

;; ---- UI ----

(declare render-node)

(defn- in-last-value-row [path clause]
  (let [v (or (clause-value clause) {})]
    [:<>
     [:input.form-control.basic-clause-value-n
      {:type "number"
       :min 1
       :value (or (get v "n") "")
       :on-change (fn [e]
                    (re-frame/dispatch [::events/set-clause-value path
                                        (assoc v "n" (fu/try-parse-number (dom/event-value e)))]))}]
     [:select.form-control.basic-clause-value-unit
      {:value (or (get v "unit") "days")
       :on-change (fn [e]
                    (re-frame/dispatch [::events/set-clause-value path
                                        (assoc v "unit" (dom/event-value e))]))}
      (for [u ["days" "weeks" "months"]]
        ^{:key u} [:option {:value u} u])]]))

(defn- date-value-input [path clause time?]
  (let [v (clause-value clause)
        ;; PTQL stores datetime as "YYYY-MM-DD HH:MM:SS"
        ;; HTML datetime-local wants "YYYY-MM-DDTHH:MM[:SS]"
        input-value (cond
                      (str/blank? v) ""
                      time? (str/replace v " " "T")
                      :else v)]
    ;; TODO: use our custom date/datetime picker for consistency?
    [:input.form-control.basic-clause-value
     {:type (if time? "datetime-local" "date")
      :value input-value
      :on-change (fn [e]
                   (let [raw (dom/event-value e)
                         ptql-value (cond
                                      (str/blank? raw)
                                      nil

                                      ;; without seconds -> append :00
                                      (and time? (= 16 (count raw)))
                                      (str (str/replace raw "T" " ") ":00")

                                      time?
                                      (str/replace raw "T" " ")

                                      :else
                                      raw)]
                     (re-frame/dispatch [::events/set-clause-value path ptql-value])))}]))

(defn- clause-row [path clause]
  (let [fields-by-entity @(re-frame/subscribe [::subs/fields-by-ptql-entity])
        source-entity @(re-frame/subscribe [::view-editor.subs/entity-type])
        fields (get fields-by-entity source-entity [])
        field-id (clause-field-id clause)
        selected-field (first (filter #(= (:id %) field-id) fields))
        op-code (clause-op-code clause)
        arity (op-arity op-code)
        field-values (:values selected-field)
        valid-ops (valid-ops-for-type (:type selected-field))
        ;; NOTE: for cases when invalid op was already in the view
        visible-ops (if (and op-code (not (valid-ops op-code)))
                      (conj valid-ops op-code)
                      valid-ops)]
    [:div.basic-clause-row
     [selector2/new-select
      {:items fields
       :value selected-field
       :label-fn :name
       :key-fn :id
       :placeholder (get-translation "view-editor.basic.pick-field" "Pick a field")
       :on-change #(re-frame/dispatch [::events/set-clause-field path (:id %) (:type %)])}]
     [:select.form-control.basic-clause-op
      {:value (or op-code "")
       :on-change #(re-frame/dispatch [::events/set-clause-op path (dom/event-value %)])}
      (for [{:keys [code]} op-codes
            :when (visible-ops code)]
        ^{:key code} [:option {:value code} code])]
     [:div.basic-clause-value-cell
      (case arity
        :in-last [in-last-value-row path clause]
        :binary (let [v (clause-value clause)
                      field-type (:type selected-field)]
                  (cond
                    (= "date" field-type)
                    [date-value-input path clause false]

                    (#{"date-time" "datetime"} field-type)
                    [date-value-input path clause true]

                    (seq field-values)
                    [selector2/new-select
                     {:items (mapv (fn [val] {:id val :name val}) field-values)
                      :value (when v {:id v :name v})
                      :label-fn :name
                      :key-fn :id
                      :placeholder (get-translation "view-editor.basic.pick-value" "Pick a value")
                      :on-change #(re-frame/dispatch [::events/set-clause-value path (:id %)])}]

                    :else
                    [:input.form-control.basic-clause-value
                     {:type "text"
                      :value (or v "")
                      :on-change #(re-frame/dispatch [::events/set-clause-value path (dom/event-value %)])}]))
        nil)]
     [buttons/icon-button
      {:icon [icons/mux-x]
       :tooltip (get-translation "view-editor.basic.remove" "Remove")
       :on-click #(re-frame/dispatch [::events/remove-at path])}]]))

(defn- group-actions [path]
  [:div.basic-group-actions
   [buttons/button
    {:tertiary? true
     :icon [icons/mux-plus-circle]
     :text (get-translation "view-editor.basic.add-condition" "Add condition")
     :on-click #(re-frame/dispatch [::events/add-clause path])}]
   [buttons/button
    {:tertiary? true
     :icon [icons/mux-plus-circle]
     :text (get-translation "view-editor.basic.add-group" "Add group")
     :on-click #(re-frame/dispatch [::events/add-group path])}]])

(defn- group-section [path group]
  (let [combinator (first group)
        children (rest group)
        hide-wrapper? (and (= path [])
                           (= 1 (count children))
                           (clause? (first children)))]
    (if hide-wrapper?
      [:<>
       (map-indexed (fn [i child]
                      ^{:key i} [render-node (conj path i) child])
                    children)
       [group-actions path]]
      [:div.basic-group {:class (when (= "or" combinator) "is-or")}
       [:div.basic-group-header
        [:select.form-control.basic-group-combinator
         {:class (when (= "or" combinator) "is-or")
          :value combinator
          :on-change (fn [e]
                       (re-frame/dispatch [::events/set-group-combinator path
                                           (keyword (dom/event-value e))]))}
         [:option {:value "and"} (get-translation "view-editor.basic.match-all" "Match ALL")]
         [:option {:value "or"} (get-translation "view-editor.basic.match-any" "Match ANY")]]
        (when (not= path [])
          [buttons/icon-button
           {:icon [icons/mux-x]
            :tooltip (get-translation "view-editor.basic.remove-group" "Remove group")
            :on-click #(re-frame/dispatch [::events/remove-at path])}])]
       [:div.basic-group-children
        (map-indexed (fn [i child]
                       ^{:key i} [render-node (conj path i) child])
                     children)]
       [group-actions path]])))

(defn render-node [path node]
  (cond
    (clause? node) [clause-row path node]
    (group? node) [group-section path node]))

(defn basic-filter-section [_form]
  (let [ast @(re-frame/subscribe [::view-editor.subs/basic-builder-ast])]
    [render-node [] ast]))

(comment

  (def id-fn identity)
  (def linked-id-fn (constantly identity))

  (ast->ptql id-fn linked-id-fn ["is" "priority" "High"])

  (ast->ptql id-fn linked-id-fn ["not" ["is" "priority" "High"]])

  (ast->ptql id-fn linked-id-fn ["contains" "title" "bug"])

  (ast->ptql id-fn linked-id-fn ["not" ["contains" "title" "bug"]])

  (ast->ptql id-fn linked-id-fn ["in-last" "created_at" {"n" 7 "unit" "days"}])

  (ast->ptql id-fn linked-id-fn ["empty" "assigned_to"])

  (ast->ptql id-fn linked-id-fn
             ["and"
              ["is" "priority" "High"]
              ["or" ["is" "status" "Open"] ["is" "status" "InProgress"]]])

  (ast->ptql id-fn linked-id-fn
             ["or"
              ["and" ["is" "priority" "High"] ["is" "status" "Open"]]
              ["is" "status" "Closed"]])

  (ast->ptql id-fn linked-id-fn
             ["exists-related" "Test" ["is" "run_status" "Failed"]])

  (ast->ptql id-fn linked-id-fn
             ["not" ["exists-related" "Test" nil]])

  (let [f (fn [k]
            (case k
              "f1" "Due Date"
              k))]
    (ast->ptql f linked-id-fn ["is" "f1" "2026-05-01"]))

  (fits? ["not" ["is" "priority" "High"]])
  (fits? ["not" ["exists-related" "Test" nil]])
  (fits? ["not" ["and" ["is" "priority" "High"]]])
  (fits? ["count" ["exists-related" "Test" nil]])

  (build-clause "not in last" "created_at" {"n" 7 "unit" "days"})
  (change-op ["is" "priority" "High"] "is empty")
  (change-op ["is" "priority" "High"] "≠")

  (valid-ast nil)
  (valid-ast ["and"])
  (valid-ast ["and" ["is" nil nil]])
  (valid-ast ["and" ["is" "priority" "High"]])
  (valid-ast ["and" ["is" "priority" "High"]
              ["or" ["is" "status" "Ready"] ["is" "status" "Open"]]])
  (valid-ast ["and" ["is" "priority" "High"]
              ["or" ["is" "status" "Ready"] ["is" "status" nil]]])

  (change-op ["is" "field" "value"] "in last")
  (change-op ["in-last" "field" {"n" 7 "unit" "days"}] "not in last")
  (change-op ["empty" "field"] "in last")

  (let [fields-by-entity @(re-frame/subscribe [:practitest.frontend.subs/fields-by-ptql-entity])
        display-fn-for (fn [entity]
                         (let [by-id (into {} (map (juxt :id :name))
                                           (get fields-by-entity entity []))]
                           (fn [field-id]
                             (get by-id field-id field-id))))
        source-fn (display-fn-for "Issue")]
    (ast->ptql source-fn display-fn-for
               ["and"
                ["is" "priority" "High"]
                ["exists-related" "Test" ["is" "run_status" "Failed"]]]))

  @(re-frame.core/subscribe [:practitest.frontend.subs/fields-by-ptql-entity])

  (re-frame.core/clear-subscription-cache!))
